import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
// import { useForm, Controller } from 'react-hook-form';

// VirtualDropdown Component
const VirtualDropdown = ({
   options = [],
   value = null,
   onChange = () => { },
   placeholder = 'Select...',
   multiple = false,
   displayKey = 'label',
   valueKey = 'value',
   searchable = true,
   clearable = true,
   noOptionsMessage = 'No options',
   className = '',
   pageSize = 20,
   disabled = false,
   renderOption = null,
   error = null,
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const [query, setQuery] = useState('');
   const [focusedIndex, setFocusedIndex] = useState(-1);
   const wrapperRef = useRef(null);
   const listRef = useRef(null);
   const inputRef = useRef(null);

   const filtered = useMemo(() => {
      if (!query) return options;
      const q = query.toLowerCase();
      return options.filter((o) =>
         String(o[displayKey] || '')
            .toLowerCase()
            .includes(q),
      );
   }, [options, query, displayKey]);

   const rowVirtualizer = useVirtualizer({
      count: filtered.length,
      getScrollElement: () => listRef.current,
      estimateSize: () => 56,
      overscan: 5,
   });

   const selectedItems = useMemo(() => {
      if (multiple) {
         return Array.isArray(value) ? options.filter((o) => value.includes(o[valueKey])) : [];
      }
      return options.find((o) => o[valueKey] === value) ? [options.find((o) => o[valueKey] === value)] : [];
   }, [options, value, valueKey, multiple]);

   useEffect(() => {
      const handler = (e) => {
         if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsOpen(false);
            setFocusedIndex(-1);
         }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
   }, []);

   const handleKeyDown = useCallback(
      (e) => {
         if (!isOpen) return;
         if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((i) => Math.min(i + 1, filtered.length - 1));
         } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((i) => Math.max(i - 1, 0));
         } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex >= 0 && filtered[focusedIndex]) {
               selectItem(filtered[focusedIndex]);
            }
         } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
         }
      },
      [isOpen, filtered, focusedIndex],
   );

   useEffect(() => {
      if (!isOpen) return;
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
   }, [isOpen, handleKeyDown]);

   useEffect(() => {
      if (isOpen && filtered.length > 0) {
         setTimeout(() => {
            if (!multiple && value) {
               const selectedIndex = filtered.findIndex((opt) => opt[valueKey] === value);
               if (selectedIndex >= 0) {
                  rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' });
                  setFocusedIndex(selectedIndex);
               }
            } else if (multiple && Array.isArray(value) && value.length > 0) {
               const firstSelectedIndex = filtered.findIndex((opt) => value.includes(opt[valueKey]));
               if (firstSelectedIndex >= 0) {
                  rowVirtualizer.scrollToIndex(firstSelectedIndex, { align: 'center' });
                  setFocusedIndex(firstSelectedIndex);
               }
            }
         }, 50);
      }
   }, [isOpen, value, multiple, valueKey, filtered, rowVirtualizer]);

   const toggleOpen = () => {
      if (disabled) return;
      const willOpen = !isOpen;
      setIsOpen(willOpen);

      if (willOpen) {
         setTimeout(() => inputRef.current?.focus(), 100);
      }
   };

   const clear = (e) => {
      e?.stopPropagation();
      if (multiple) onChange([]);
      else onChange(null);
      setQuery('');
   };

   function selectItem(item) {
      if (multiple) {
         const already = Array.isArray(value) ? value.includes(item[valueKey]) : false;
         if (already) {
            onChange(value.filter((v) => v !== item[valueKey]));
         } else {
            onChange([...(Array.isArray(value) ? value : []), item[valueKey]]);
         }
      } else {
         onChange(item[valueKey]);
         setIsOpen(false);
      }
      setQuery('');
      setFocusedIndex(-1);
   }

   const displayValue = () => {
      if (multiple) return null;
      if (!value) return null;
      const sel = options.find((o) => o[valueKey] === value);
      return sel || null;
   };

   const defaultRenderOption = (opt, isSelected, isFocused) => (
      <div className="flex items-center justify-between gap-3 py-3 px-4">
         <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">{opt[displayKey]}</div>
            {opt.description && <div className="text-sm text-gray-500 truncate mt-0.5">{opt.description}</div>}
         </div>
         {isSelected && (
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
               <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
               />
            </svg>
         )}
      </div>
   );

   const virtualItems = rowVirtualizer.getVirtualItems();
   const firstVisibleIndex = virtualItems[0]?.index ?? 0;
   const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? 0;

   return (
      <div ref={wrapperRef} className={`relative ${className}`}>
         <button
            type="button"
            disabled={disabled}
            onClick={toggleOpen}
            className={`w-full flex items-center gap-2 px-3 py-1.5 border rounded-lg bg-slate-800/50 transition-all ${disabled
                  ? 'opacity-50 cursor-not-allowed border-slate-700'
                  : error
                     ? 'border-red-500 ring-2 ring-red-400'
                     : isOpen
                        ? 'border-blue-500 ring-2 ring-blue-400'
                        : 'border-slate-700 hover:border-slate-700'
               }`}>
            <div className="flex-1 flex items-center gap-2 min-w-0">
               {multiple && selectedItems.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                     {selectedItems.map((s) => (
                        <div
                           key={s[valueKey]}
                           className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-sm font-medium border border-blue-200">
                           <span className="truncate">{s[displayKey]}</span>
                           <button
                              type="button"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 selectItem(s);
                              }}
                              className="hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-blue-600">
                              ×
                           </button>
                        </div>
                     ))}
                  </div>
               ) : (
                  <div className="min-w-0">
                     {displayValue() ? (
                        <div className="truncate text-white font-medium text-sm">{displayValue()[displayKey]}</div>
                     ) : (
                        <div className="truncate text-gray-500">{placeholder}</div>
                     )}
                  </div>
               )}
            </div>

            <div className="flex items-center gap-1">
               {clearable && (multiple ? selectedItems.length > 0 : Boolean(value)) && (
                  <button
                     onClick={clear}
                     type="button"
                     className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors">
                     <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path
                           fillRule="evenodd"
                           d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                           clipRule="evenodd"
                        />
                     </svg>
                  </button>
               )}
               <svg
                  className={`w-5 h-5 transition-transform text-gray-400 ${isOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                     fillRule="evenodd"
                     d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                     clipRule="evenodd"
                  />
               </svg>
            </div>
         </button>

         {error && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
               <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                     fillRule="evenodd"
                     d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                     clipRule="evenodd"
                  />
               </svg>
               {error}
            </p>
         )}

         {isOpen && (
            <div className="absolute z-50 mt-2 w-full rounded-lg shadow-2xl border border-gray-200 bg-white overflow-hidden">
               {searchable && (
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                     <div className="relative">
                        <svg
                           className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                           />
                        </svg>
                        <input
                           ref={inputRef}
                           value={query}
                           onChange={(e) => setQuery(e.target.value)}
                           className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                           placeholder="Search..."
                        />
                     </div>
                  </div>
               )}

               <div ref={listRef} className="max-h-80 overflow-auto">
                  {filtered.length === 0 ? (
                     <div className="p-12 text-center">
                        <svg
                           className="mx-auto w-12 h-12 text-gray-300 mb-3"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                           />
                        </svg>
                        <p className="text-sm text-gray-500">{noOptionsMessage}</p>
                     </div>
                  ) : (
                     <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                           const opt = filtered[virtualRow.index];
                           const isSelected = multiple
                              ? Array.isArray(value) && value.includes(opt[valueKey])
                              : value === opt[valueKey];
                           const isFocused = virtualRow.index === focusedIndex;

                           return (
                              <div
                                 key={virtualRow.key}
                                 style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                 }}>
                                 <button
                                    type="button"
                                    onMouseEnter={() => setFocusedIndex(virtualRow.index)}
                                    onClick={() => selectItem(opt)}
                                    className={`w-full text-left transition-colors border-b border-gray-100 ${isSelected ? 'bg-blue-50' : isFocused ? 'bg-gray-100' : 'hover:bg-gray-50'
                                       }`}>
                                    {renderOption
                                       ? renderOption(opt, isSelected, isFocused)
                                       : defaultRenderOption(opt, isSelected, isFocused)}
                                 </button>
                              </div>
                           );
                        })}
                     </div>
                  )}
               </div>

               {filtered.length > 0 && (
                  <div className="px-4 py-2.5 text-xs bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                     <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">
                           Showing {firstVisibleIndex + 1}-{Math.min(lastVisibleIndex + 1, filtered.length)} of{' '}
                           {filtered.length}
                        </span>
                     </div>
                     <div className="text-gray-400">
                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑</kbd>
                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs ml-1">↓</kbd>
                     </div>
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default VirtualDropdown;
